import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { getMaxMedicalProfilesCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('UseOrganizationMaxMedicalProfiles', {
    usageLoadFailure: 'Failed to load max profile usage',
});

export type UseOrganizationAllowedMedicalProfileUsageArgs = {};

export type UseOrganizationAllowedMedicalProfileUsageReturnValue = {
    allowedProfileUsage: APITypes.organizationService.GetOrganizationAllowedMedicalProfileUsageResponse | null;
    isLoading: boolean;
    hasHitLimit: boolean;
};

export default function useOrganizationAllowedMedicalProfileUsage({}: UseOrganizationAllowedMedicalProfileUsageArgs = {}): UseOrganizationAllowedMedicalProfileUsageReturnValue {
    const organizationId = useOrganizationId();
    const organizationMaxMedicalProfilesQuery = useQuery({
        queryKey: getMaxMedicalProfilesCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationAllowedMedicalProfileUsageResponse>(
                urls.api.organizations.usage.allowedMedicalProfiles(organizationId),
            );
        },
    });
    useQueryErrorNotify(organizationMaxMedicalProfilesQuery, intl('usageLoadFailure'));

    return {
        allowedProfileUsage: organizationMaxMedicalProfilesQuery.data || null,
        isLoading: organizationMaxMedicalProfilesQuery.isLoading,
        hasHitLimit:
            organizationMaxMedicalProfilesQuery.data?.type === 'limited' &&
            organizationMaxMedicalProfilesQuery.data.currentMedicalProfileCount >=
                organizationMaxMedicalProfilesQuery.data.maxAllowedMedicalProfileCount,
    };
}
